import Konva from 'konva';
import { memo, useCallback, useMemo, useRef } from 'react';
import { Group } from 'react-konva';
import { MidpointHandle } from './MidpointHandle';
import { VertexHandle } from './VertexHandle';
import { AreaShape, LineShape } from './ZoneShapes';
import { CanvasDimensions, Point, SpecialZoneType } from './types';
import { useCursorHandlers } from './useCursorHandlers';
import { useZoneDragHandlers } from './useZoneDragHandlers';
import { calculateMidpoints, isLineZoneType, pointsToFlatArray } from './utils';

interface Props {
  zoneId: string;
  points: Point[];
  bounds: CanvasDimensions;
  onPointsChange: (zoneId: string, points: Point[]) => void;
  type: SpecialZoneType;
  /** Drawn under the icon, for zones that have a name */
  label?: string;
  isSelected: boolean;
  onSelect: (zoneId: string) => void;
  canEdit: boolean;
}

const LINE_MIN_POINTS = 2;
const POLYGON_MIN_POINTS = 4;

export const ZonePolygon = memo(function ZonePolygon({
  zoneId,
  points,
  bounds,
  onPointsChange,
  type,
  label,
  isSelected,
  onSelect,
  canEdit,
}: Props) {
  const groupRef = useRef<Konva.Group>(null);
  const lineRef = useRef<Konva.Line>(null);
  const isLine = isLineZoneType(type);

  const midpoints = useMemo(
    () => calculateMidpoints(points, !isLine),
    [points, isLine]
  );
  const flatPoints = useMemo(() => pointsToFlatArray(points), [points]);
  const shapeProps = { points, flatPoints, label, isSelected, lineRef };

  const handleZonePointsChange = useCallback(
    (newPoints: Point[]) => {
      onPointsChange(zoneId, newPoints);
    },
    [zoneId, onPointsChange]
  );

  const {
    handleDragStart,
    handleVertexDragMove,
    handleVertexDragEnd,
    handleVertexDoubleClick,
    handleMidpointDragMove,
    handleMidpointDragEnd,
    handlePolygonDragEnd,
  } = useZoneDragHandlers({
    points,
    bounds,
    onPointsChange: handleZonePointsChange,
    groupRef,
    lineRef,
    closed: !isLine,
    minPoints: isLine ? LINE_MIN_POINTS : POLYGON_MIN_POINTS,
  });

  const { crosshairCursorEnter, resizeCursorEnter, handleCursorLeave } =
    useCursorHandlers();

  const handleClick = useCallback(() => {
    if (canEdit) {
      onSelect(zoneId);
    }
  }, [zoneId, onSelect, canEdit]);

  const handleDragStartWithSelect = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      onSelect(zoneId);
      handleDragStart(e);
    },
    [zoneId, onSelect, handleDragStart]
  );

  return (
    <Group
      ref={groupRef}
      draggable={canEdit}
      onDragStart={handleDragStartWithSelect}
      onDragEnd={handlePolygonDragEnd}
      onClick={handleClick}
      onTap={handleClick}
    >
      {isLineZoneType(type) ? (
        <LineShape {...shapeProps} />
      ) : (
        <AreaShape type={type} {...shapeProps} />
      )}
      {/* Only render handles when zone is selected */}
      {isSelected && (
        <>
          {/* Render midpoints first so vertices appear on top */}
          {midpoints.map((midpoint, index) => (
            <MidpointHandle
              key={`midpoint-${index}`}
              position={midpoint}
              index={index}
              onDragStart={handleDragStart}
              onDragMove={handleMidpointDragMove}
              onDragEnd={handleMidpointDragEnd}
              onMouseEnter={crosshairCursorEnter}
              onMouseLeave={handleCursorLeave}
            />
          ))}
          {points.map((point, index) => (
            <VertexHandle
              key={`vertex-${index}`}
              point={point}
              index={index}
              onDragStart={handleDragStart}
              onDragMove={handleVertexDragMove}
              onDragEnd={handleVertexDragEnd}
              onDoubleClick={handleVertexDoubleClick}
              onMouseEnter={resizeCursorEnter}
              onMouseLeave={handleCursorLeave}
            />
          ))}
        </>
      )}
    </Group>
  );
});

export default ZonePolygon;
